import { useGraviChatStore } from '@/components/GraviChat/store'
import type { GraviDeltaEvent } from '@/components/GraviChat/types'
import { streamSSE } from '@/utils/sseStream'
import { useCallback, useRef } from 'react'

/**
 * Opens the gravitutor SSE stream for a task and dispatches each delta into the
 * GraviChat store, persisting progress to `sessionStorage` for refresh recovery.
 * Ported from S&D `GraviChat/hooks/useGraviStream.ts`, but the wire-level fetch +
 * frame parsing + reconnect-by-cursor now live in the reusable `@/utils/sseStream`
 * util.
 */

export type UseGraviStreamStartArgs = { threadId: string; taskId: string }

export type UseGraviStreamOptions = {
  /** Reconnect backoff schedule (ms) passed through to the SSE util. */
  reconnectBackoffMs?: number[]
  /** Called once after the stream reaches a terminal state (clean or error). */
  onSettled?: () => void
}

const DEFAULT_BACKOFF = [1000, 2000, 5000, 10000]

export function useGraviStream(opts: UseGraviStreamOptions = {}) {
  const backoff = opts.reconnectBackoffMs ?? DEFAULT_BACKOFF
  const onSettled = opts.onSettled
  const abortRef = useRef<AbortController | null>(null)

  const stop = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
  }, [])

  const start = useCallback(
    async ({ threadId, taskId }: UseGraviStreamStartArgs) => {
      stop()
      const store = useGraviChatStore.getState()
      store.resetStreamState()
      store.setActiveTask({ threadId, taskId, cursor: 0 })
      store.setStatus('streaming')

      const controller = new AbortController()
      abortRef.current = controller

      try {
        for await (const evt of streamSSE<GraviDeltaEvent>(
          `/gravitutor/tasks/${taskId}/stream`,
          { signal: controller.signal, reconnectBackoffMs: backoff },
        )) {
          useGraviChatStore.getState().onDelta(evt)
          sessionStorage.setItem(
            `gravi.active.${threadId}`,
            JSON.stringify({ taskId, cursor: evt.cursor }),
          )
        }

        // Clean upstream close: the backend ended the stream because the task
        // reached a terminal state. This is "done".
        if (!controller.signal.aborted) {
          useGraviChatStore.getState().setStatus('idle')
          sessionStorage.removeItem(`gravi.active.${threadId}`)
          onSettled?.()
        }
      } catch {
        if (controller.signal.aborted) return
        // The util exhausted its reconnect budget — surface a hard error.
        useGraviChatStore.getState().setStatus('error')
        onSettled?.()
      }
    },
    [backoff, onSettled, stop],
  )

  return { start, stop }
}
